import React, { useCallback, useState } from 'react';
import { DatePicker } from 'antd';
import zhCN from 'antd/es/date-picker/locale/zh_CN';
import dayjs, { type Dayjs } from 'dayjs';
import 'dayjs/locale/zh-cn';
import type { InputNode } from '../types';
import { RequiredMark } from './RequiredMark';

dayjs.locale('zh-cn');

const { RangePicker } = DatePicker;

type Props = {
  node: InputNode;
  value: string | undefined;
  onChange: (key: string, value: string) => void;
  disabled?: boolean;
  error?: string;
};

const parseValue = (v: string | undefined): [Dayjs, Dayjs] | null => {
  if (!v) return null;
  const parts = v.split(',');
  if (parts.length !== 2 || !parts[0] || !parts[1]) return null;
  const a = dayjs(parts[0]);
  const b = dayjs(parts[1]);
  return a.isValid() && b.isValid() ? [a, b] : null;
};

/**
 * V1 parity (RichTextEditor/nodes/datepicker/DatePickerPortal.tsx):
 * - RangePicker（日期范围），value 契约 'YYYY-MM-DD,YYYY-MM-DD'
 * - limitRange：选了首日后，第二日限制在首日 ±N 天内（用 onCalendarChange + hackValue 联动）
 * - disableFuture：禁止选未来日
 * - zh_CN locale + popupClassName；浮层容器由外层 ConfigProvider 统一收口（见 index.tsx popup-host）
 */
export const DatePickerNode: React.FC<Props> = React.memo(
  ({ node, value, onChange, disabled, error }) => {
    const dates = parseValue(value);
    const [hackValue, setHackValue] = useState<
      [Dayjs | null, Dayjs | null] | null
    >(null);

    const handleChange = useCallback(
      (val: [Dayjs | null, Dayjs | null] | null) => {
        setHackValue(null);
        if (val && val[0] && val[1]) {
          onChange(
            node.key!,
            `${val[0].format('YYYY-MM-DD')},${val[1].format('YYYY-MM-DD')}`,
          );
        } else {
          onChange(node.key!, '');
        }
      },
      [node.key, onChange],
    );

    const handleCalendarChange = useCallback(
      (val: [Dayjs | null, Dayjs | null] | null) => setHackValue(val),
      [],
    );

    const handleOpenChange = useCallback(
      (open: boolean) => {
        if (open) setHackValue(dates ? [dates[0], dates[1]] : null);
        else setHackValue(null);
      },
      [dates],
    );

    const disabledDate = useCallback(
      (current: Dayjs) => {
        if (!current) return false;
        if (node.disableFuture && current.isAfter(dayjs(), 'day')) return true;
        if (typeof node.limitRange !== 'number' || !hackValue || !hackValue[0]) {
          return false;
        }
        const diff = Math.abs(current.diff(hackValue[0], 'days'));
        return diff > node.limitRange;
      },
      [node.disableFuture, node.limitRange, hackValue],
    );

    return (
      <RequiredMark node={node}>
      <span
        className={`rte-datepicker-host ${error ? 'rte-datepicker-error' : ''}`}
        data-type="datepicker"
        data-key={node.key}
      >
        <RangePicker
          data-type="datepicker"
          data-key={node.key}
          value={dates}
          disabledDate={disabledDate}
          onCalendarChange={handleCalendarChange}
          onChange={handleChange}
          onOpenChange={handleOpenChange}
          disabled={disabled}
          placeholder={[
            node.placeholder || '开始日期',
            node.placeholder || '结束日期',
          ]}
          size="small"
          variant="borderless"
          allowClear
          allowEmpty={[true, true]}
          locale={zhCN}
          classNames={{ popup: { root: 'rte-datepicker-dropdown' } }}
        />
      </span>
      </RequiredMark>
    );
  },
);
DatePickerNode.displayName = 'DatePickerNode';
